import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "@auth";
import { useGetLifestyleCategories, useGetLifestyleTransactions, useGetPartnerPaymentMethods } from "@queries";
import { useApproveLifestyleTransaction, useRejectLifestyleTransaction } from "@mutations";
import { 
  LSATransaction, 
  LSATransactionStatus, 
  TransactionFilter, 
  PaginationInfo,
} from "../../../../types";
import { createApproveTransactionHandler, createRejectTransactionHandler } from "../transaction-actions";

export interface LifestyleTransactionsPageTemplateProps {
  transactions: LSATransaction[];
  pagination: PaginationInfo;
  isInitialLoading: boolean;
  isPageLoading: boolean;
  error: string | null;
  selectedTab: LSATransactionStatus | 'all';
  setSelectedTab: (tab: LSATransactionStatus | 'all') => void;
  filters: TransactionFilter;
  setFilters: (filters: TransactionFilter) => void;
  onApproveTransaction: (transactionId: string, rowIndex: number) => Promise<void>;
  onRejectTransaction: (transactionId: string, rowIndex: number) => void;
  approvingTransactionIds: Set<string>;
  rejectingTransactionIds: Set<string>;
  activeRowId: string | null;
  activeRowIndex: number | null;
  approveErrorMessage: string | null;
  onDismissApproveError: () => void;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
  onPageChange: (page: number) => void;
  onReturnPressed: () => void;
  onRefresh: () => Promise<void>;
}

export const useLifestyleTransactionsPage = (): LifestyleTransactionsPageTemplateProps => {
  const navigate = useNavigate();
  const location = useLocation();
  const { partner, billingDetails } = useContext(AuthContext);
  const approveLifestyleTransaction = useApproveLifestyleTransaction();
  const rejectLifestyleTransaction = useRejectLifestyleTransaction();
  const { getPartnerPaymentMethods, creditCards } = useGetPartnerPaymentMethods();
  
  // Get group ID from URL params or state
  const groupId = new URLSearchParams(location.search).get('groupId') || 
                  location.state?.groupId || 
                  partner?.lsaGroups?.[0]?.groupId;

  const { getLifestyleTransactions, transactions: rawTransactions, loading, error } = useGetLifestyleTransactions();
  const { getLifestyleCategories } = useGetLifestyleCategories();
  const [selectedTab, setSelectedTab] = useState<LSATransactionStatus | 'all'>('all');
  const [filters, setFilters] = useState<TransactionFilter>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15);
  const [sortField, setSortField] = useState('receiptDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [parsedTransactions, setParsedTransactions] = useState<LSATransaction[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [approvingTransactionIds, setApprovingTransactionIds] = useState<Set<string>>(new Set());
  const [rejectingTransactionIds, setRejectingTransactionIds] = useState<Set<string>>(new Set());
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
  const [approveErrorMessage, setApproveErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Ensure no residual selection/highlight on first render (or hot reload)
    setActiveRowId(null);
    setActiveRowIndex(null);
    setApprovingTransactionIds(new Set());
    setRejectingTransactionIds(new Set());
  }, []);

  useEffect(() => {
    getLifestyleCategories();
  }, []);

  const resolvePaymentId = async (): Promise<string | null> => {
    const existing = billingDetails?.creditCards?.[0]?.paymentId ?? creditCards?.[0]?.paymentId;
    if (existing) {
      return existing;
    }
    try {
      const result = await getPartnerPaymentMethods();
      const fetched = (result as any)?.data?.getPartnerPaymentMethods?.[0]?.paymentId;
      return fetched ?? null;
    } catch (err) {
      console.error("Error fetching partner payment methods:", err);
      return null;
    }
  };

  // Parse transactions data from stringified JSON
  useEffect(() => {
    if (rawTransactions) {
      try {
        const { docs, totalDocs } = rawTransactions;
        const parsedDocs = JSON.parse(docs);
        console.log('parsedDocs', parsedDocs);
        console.log('totalDocs', totalDocs);
        console.log('totalDocs type', typeof totalDocs);
        const transactions: LSATransaction[] = parsedDocs.map((doc: any) => ({
          id: doc.id,
          currency: doc.currency,
          userId: doc.userId,
          partnerId: doc.partnerId,
          groupId: doc.groupId,
          categoryId: doc.categoryId,
          costAmount: doc.costAmount,
          receiptDate: doc.receiptDate,
          brandName: doc.brandName,
          coveredAmount: {
            currency: doc.coveredAmount?.currency,
            value: doc.coveredAmount?.value / 100|| doc.coveredAmount / 100,
          },
          paymentId: doc.paymentId,
          maxCoveragePercentage: doc.maxCoveragePercentage,
          receiptS3URL: doc.receiptS3URL,
          firstName: doc.firstName,
          lastName: doc.lastName,
          emailAddress: doc.emailAddress,
          partnerReceipt: doc.partnerReceipt,
          status: doc.status as LSATransactionStatus,
          createdAt: doc.createdAt,
        }));
        
        setParsedTransactions(transactions);
        setTotalCount( totalDocs ?? 0 );
        setHasLoadedOnce(true);
        setIsPageLoading(false);
      } catch (err) {
        console.error('Error parsing transactions:', err);
        setParsedTransactions([]);
        setTotalCount(0);
        setHasLoadedOnce(true);
        setIsPageLoading(false);
      }
    } else if (!hasLoadedOnce) {
      setParsedTransactions([]);
      setTotalCount(0);
    }
  }, [rawTransactions, partner?.id, groupId, hasLoadedOnce]);

  // Pagination info
  const computedTotalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1;
  const pagination: PaginationInfo = {
    page: currentPage,
    limit: pageSize,
    totalCount,
    totalPages: computedTotalPages,
    hasNextPage: totalCount > 0 && currentPage < computedTotalPages,
    hasPreviousPage: currentPage > 1,
  };

  // Load transactions when filters change
  useEffect(() => {
    if (groupId) {
      const status = filters?.status ?? undefined;
      const offset = (currentPage - 1) * pageSize;
      getLifestyleTransactions({
        variables: {
          status,
          offset,
          limit: pageSize,
        }
      });
    }
  }, [groupId, selectedTab, currentPage, filters, pageSize]);

  // Filter transactions by selected tab
  const filteredTransactions = parsedTransactions.filter(tx => {
    if (selectedTab !== 'all' && tx.status !== selectedTab) return false;
    return true;
  });

  const beginPageLoad = () => {
    if (hasLoadedOnce) {
      setIsPageLoading(true);
    }
  };

  const handleTabChange = (tab: LSATransactionStatus | 'all') => {
    beginPageLoad();
    setSelectedTab(tab);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page === currentPage) {
      return;
    }
    const maxPage = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1;
    if (page > maxPage) {
      return;
    }
    beginPageLoad();
    setCurrentPage(page);
  };

  const handleReturnPressed = () => {
    navigate(-1);
  };

  const handleRefresh = async () => {
    if (!groupId) {
      return;
    }
    setIsPageLoading(true);
    const offset = (currentPage - 1) * pageSize;
    console.log("refreshing table...");
    try {
      await getLifestyleTransactions({
        variables: {
          status: undefined,
          offset,
          limit: pageSize,
        },
      });
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const approveTransaction = createApproveTransactionHandler({
    resolvePaymentId,
    approveMutation: approveLifestyleTransaction,
    onStart: (transactionId, rowIndex) => {
      setApprovingTransactionIds((prev) => {
        const next = new Set(prev);
        next.add(transactionId);
        return next;
      });
      setActiveRowId(transactionId);
      setActiveRowIndex(rowIndex ?? null);
    },
    onMissingPaymentMethod: (message) => setApproveErrorMessage(message),
    onError: (message) => setApproveErrorMessage(message),
    onSuccess: async (transactionId) => {
      const offset = (currentPage - 1) * pageSize;
      setParsedTransactions((prev) =>
        prev.map((tx) =>
          tx.id === transactionId ? { ...tx, status: LSATransactionStatus.Completed } : tx,
        ),
      );
      setIsPageLoading(true);
      await getLifestyleTransactions({
        variables: {
          status: undefined,
          offset,
          limit: pageSize,
        },
      });
    },
    onFinally: (transactionId) => {
      setIsPageLoading(false);
      setApprovingTransactionIds((prev) => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
      setActiveRowId(null);
      setActiveRowIndex(null);
    },
  });

  const rejectTransaction = createRejectTransactionHandler({
    rejectMutation: rejectLifestyleTransaction,
    onStart: (transactionId, rowIndex) => {
      setRejectingTransactionIds((prev) => {
        const next = new Set(prev);
        next.add(transactionId);
        return next;
      });
      setActiveRowId(transactionId);
      setActiveRowIndex(rowIndex ?? null);
    },
    onError: (message) => setApproveErrorMessage(message),
    onSuccess: async (transactionId) => {
      const offset = (currentPage - 1) * pageSize;
      setIsPageLoading(true);
      setParsedTransactions((prev) =>
        prev.map((tx) =>
          tx.id === transactionId ? { ...tx, status: LSATransactionStatus.Denied } : tx,
        ),
      );
      await getLifestyleTransactions({
        variables: {
          status: undefined,
          offset,
          limit: pageSize,
        },
      });
    },
    onFinally: (transactionId) => {
      setIsPageLoading(false);
      setRejectingTransactionIds((prev) => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
      setActiveRowId(null);
      setActiveRowIndex(null);
    },
  });

  const handleApproveTransaction = (transactionId: string, rowIndex: number) =>
    approveTransaction(transactionId, rowIndex);

  const handleRejectTransaction = (transactionId: string, rowIndex: number) =>
    rejectTransaction(transactionId, rowIndex);

  const isInitialLoading = loading && !hasLoadedOnce;

  return {
    transactions: filteredTransactions,
    pagination,
    isInitialLoading,
    isPageLoading,
    error: error?.message || null,
    selectedTab,
    setSelectedTab: handleTabChange,
    filters,
    setFilters,
    onApproveTransaction: handleApproveTransaction,
    onRejectTransaction: handleRejectTransaction,
    approvingTransactionIds,
    rejectingTransactionIds,
    activeRowId,
    activeRowIndex,
    approveErrorMessage,
    onDismissApproveError: () => setApproveErrorMessage(null),
    sortField,
    sortDirection,
    onSort: handleSort,
    onPageChange: handlePageChange,
    onReturnPressed: handleReturnPressed,
    onRefresh: handleRefresh,
  };
};
